import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Save, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const SOCIAL_KEYS = ["email", "phone", "twitch", "instagram", "facebook", "tiktok", "youtube"];

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  location: "",
  logo_url: "",
  logo_nav: "",
  logo_footer: "",
  twitch_channel: "",
  profile_summary: "",
  social_links: SOCIAL_KEYS.reduce((acc, k) => ({ ...acc, [k]: "" }), {}),
};

export default function AdminProfile() {
  const { apiFetch } = useAuth();
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiFetch("GET", "/api/admin/profile");
      setForm({
        ...EMPTY_FORM,
        ...data,
        social_links: { ...EMPTY_FORM.social_links, ...(data.social_links || {}) },
      });
    } catch (err) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const handleSocialChange = (key) => (e) =>
    setForm((prev) => ({ ...prev, social_links: { ...prev.social_links, [key]: e.target.value } }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        location: form.location,
        logo_url: form.logo_url,
        logo_nav: form.logo_nav,
        logo_footer: form.logo_footer,
        twitch_channel: form.twitch_channel,
        profile_summary: form.profile_summary,
        social_links: form.social_links,
      };
      await apiFetch("PUT", "/api/admin/profile", payload);
      toast.success("Profile saved");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to save profile";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]" data-testid="admin-profile-loading">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-profile-page">
      <header>
        <span className="mono-label text-primary text-[10px]">Editing</span>
        <h2 className="text-2xl font-semibold mt-1" style={{ fontFamily: "var(--font-display)" }}>
          Profile
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Core identity, contact, and social links shown across the public site.
        </p>
      </header>

      <form onSubmit={onSubmit} className="space-y-6" data-testid="admin-profile-form">
        <Card className="p-6 bg-card border-border space-y-4">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Identity</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={form.name} onChange={handleChange("name")} data-testid="profile-input-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={handleChange("email")} data-testid="profile-input-email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" value={form.phone} onChange={handleChange("phone")} data-testid="profile-input-phone" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input id="location" value={form.location} onChange={handleChange("location")} data-testid="profile-input-location" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="twitch_channel">Twitch channel</Label>
              <Input id="twitch_channel" value={form.twitch_channel} onChange={handleChange("twitch_channel")} data-testid="profile-input-twitch" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="profile_summary">Summary</Label>
            <Textarea
              id="profile_summary"
              rows={4}
              value={form.profile_summary}
              onChange={handleChange("profile_summary")}
              data-testid="profile-input-summary"
            />
          </div>
        </Card>

        <Card className="p-6 bg-card border-border space-y-4">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Logos</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="logo_url">Hero logo URL</Label>
              <Input id="logo_url" value={form.logo_url} onChange={handleChange("logo_url")} data-testid="profile-input-logo-url" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="logo_nav">Nav logo URL</Label>
              <Input id="logo_nav" value={form.logo_nav} onChange={handleChange("logo_nav")} data-testid="profile-input-logo-nav" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="logo_footer">Footer logo URL</Label>
              <Input id="logo_footer" value={form.logo_footer} onChange={handleChange("logo_footer")} data-testid="profile-input-logo-footer" />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-card border-border space-y-4">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Social links</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {SOCIAL_KEYS.map((key) => (
              <div className="space-y-2" key={key}>
                <Label htmlFor={`social-${key}`} className="capitalize">
                  {key}
                </Label>
                <Input
                  id={`social-${key}`}
                  value={form.social_links[key] || ""}
                  onChange={handleSocialChange(key)}
                  placeholder={key === "email" ? "contact@tylegn.com" : "https://…"}
                  data-testid={`profile-social-${key}`}
                />
              </div>
            ))}
          </div>
        </Card>

        <Separator />
        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="ghost" onClick={load} disabled={saving} data-testid="profile-reset-button">
            Reset
          </Button>
          <Button type="submit" disabled={saving} data-testid="profile-save-button">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" /> Save changes
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
